/**
 * The live city the user is on, kept in localStorage as a pair:
 * `lastSelectedCity` and `lastSelectedCountry`. `getPreferredCity` reads the city; onboarding
 * reads both back (`getStoredOnboardingLocation`) and saves them to the backend profile, so
 * a country left over from a previous city is a wrong answer, where no country is only a
 * missing one.
 *
 * City names compare case-insensitively, like `resolveAvailableCity` and the backend.
 */

const sameCity = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

/**
 * The country of `city` as `/cities` lists it: a string, `null` when the city is listed
 * without one, or `undefined` when it is not listed (or the list has not loaded yet).
 */
export const cityCountry = (city, cityOptions) => {
  const option = (cityOptions || []).find((candidate) => sameCity(candidate.value, city));
  return option ? option.country ?? null : undefined;
};

/**
 * The one writer of the live city.
 *
 * `country`: a string is stored, `null` (the city has none) removes it, and `undefined`
 * (not known here) keeps it only while the city stays the same.
 */
export const rememberSelectedCity = (city, country) => {
  if (!city) return;
  const cityChanged = !sameCity(localStorage.getItem('lastSelectedCity'), city);
  localStorage.setItem('lastSelectedCity', city);
  if (country) {
    localStorage.setItem('lastSelectedCountry', country);
  } else if (country === null || cityChanged) {
    localStorage.removeItem('lastSelectedCountry');
  }
};

/** `rememberSelectedCity` for a city picked from the `/cities` list, which knows its country. */
export const rememberListedCity = (city, cityOptions) =>
  rememberSelectedCity(city, cityCountry(city, cityOptions));
